/* eslint-disable @typescript-eslint/no-explicit-any */
import { faArrowDown } from "@fortawesome/pro-regular-svg-icons";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";

import { ButtonColor } from "@/components/Button/types";

import { debounce } from "@/utils/debounce";

import { PermissionRoles } from "@/enum";

import { ScenarioMessageTypes } from "../../enums/ScenarioMessageTypes.enum";
import { ScenarioMessageInfo } from "../../interfaces/ScenarioMessageInfo.interface";
import ChatInputField from "./ChatInputField";
import EmptyChat from "./EmptyChat";
import ChatDecision from "./MessageTypes/ChatDecision";
import ChatMessage from "./MessageTypes/ChatMessage";
import EndScenarioMessage from "./MessageTypes/EndScenarioMessage";
import ServiceMessage from "./MessageTypes/ServiceMessage";

interface IChatProps {
	handleAction?: (type: ScenarioMessageTypes, messageId: string, data: unknown) => void;
	sendMessage: (message: string) => void;
	messages: ScenarioMessageInfo[];
	chatBodyClassName?: string;
	chatInputClassName?: string;
	type?: "tasks" | "protocol" | "chat";
	currentPhaseId?: number;
	isLastPhase?: boolean;
	chatPermissions?: PermissionRoles[];
	chatContainerClassName?: string;
	/**
	 * Replaces the chat input, `null` for none. The Aufgaben tab writes protocol entries with the
	 * Protokoll form's own input (BCM360-381); the chat keeps its input.
	 */
	input?: ReactNode;
}

export default function Chat({
	messages,
	handleAction,
	sendMessage,
	chatBodyClassName,
	chatInputClassName,
	type = "chat",
	currentPhaseId,
	isLastPhase,
	chatPermissions,
	chatContainerClassName,
	input,
}: IChatProps) {
	const chatContainerRef = useRef<HTMLDivElement>(null);
	const previousMessageLength = useRef<number>(0);
	const [showScrollButton, setShowScrollButton] = useState(false);

	useEffect(() => {
		if (!chatContainerRef.current || previousMessageLength.current === messages.length) {
			return;
		}

		previousMessageLength.current = messages.length;
		chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
	}, [messages]);

	const handleScroll = useMemo(
		() =>
			// eslint-disable-next-line react-hooks/refs
			debounce(() => {
				if (!chatContainerRef.current) {
					return;
				}

				const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;

				setShowScrollButton(scrollTop + clientHeight < scrollHeight - 500);
			}, 100),
		[]
	);

	const scrollToBottom = () => {
		setShowScrollButton(false);
		chatContainerRef.current?.scrollTo({
			top: chatContainerRef.current.scrollHeight,
			behavior: "smooth",
		});
	};

	// The Aufgaben tab lists what still asks for action: the open decisions, and the end of the run.
	// Protocol entries are shown in the Protokoll tab only (BCM360-388).
	const openDecisionsAndEnd = [
		...messages.filter((message) => message.type === ScenarioMessageTypes.END),
		...messages.filter((message) => {
			if (message.type === ScenarioMessageTypes.DECISION && !!message.data.finalDecision) {
				return false;
			}

			if (message.type !== ScenarioMessageTypes.DECISION) {
				return false;
			}

			return true;
		}),
	];

	const filteredMessagesWithouCurrentDesicions = messages.filter((message) => {
		if (message.type === ScenarioMessageTypes.DECISION && !message.data.finalDecision) {
			return false;
		}

		return true;
	});

	const shownMessages =
		type === "tasks" ? openDecisionsAndEnd : filteredMessagesWithouCurrentDesicions;

	return (
		<div
			className={`flex flex-col h-full justify-between w-full relative gap-1 flex-1 bg-surface overflow-y-auto p-2 rounded-b-[20px] ${
				chatContainerClassName ?? ""
			}`}
		>
			<div
				ref={chatContainerRef}
				className={`flex-1 w-full pb-2 flex flex-col overflow-y-auto custom-scrollbar items-center transition-all  ${
					chatBodyClassName ?? ""
				}`}
				tabIndex={0}
				onScroll={handleScroll}
			>
				{shownMessages.length ? (
					<div className="px-4 flex flex-col mt-auto w-full flex-1">
						{/* {<BlockTitle label="Today" />} */}
						{shownMessages.map((messageInfo, indx) => {
							if (messageInfo.type === ScenarioMessageTypes.MESSAGE) {
								const isTheSameUser =
									indx !== 0 && messages[indx - 1].data?.userId === messageInfo?.data?.userId;
								const isLessThanOneMinute =
									indx !== 0 && messageInfo.timestamp - messages[indx - 1].timestamp < 60000;

								return (
									<ChatMessage
										key={messageInfo.id}
										isProtocol={type !== "chat"}
										isStacked={isTheSameUser && isLessThanOneMinute}
										messageInfo={messageInfo}
									/>
								);
							} else if (messageInfo.type === ScenarioMessageTypes.DECISION) {
								return (
									<ChatDecision
										key={indx}
										chatContainerRef={chatContainerRef}
										currentPhaseId={currentPhaseId ?? -1}
										decisionInfo={messageInfo.data}
										isLastPhase={!!isLastPhase}
										messageId={messageInfo.id}
										requireExplanation={messageInfo?.data?.requireExplanation}
										timestamp={messageInfo?.timestamp}
										onActionClick={handleAction ?? console.log}
									/>
								);
							} else if (messageInfo.type === ScenarioMessageTypes.END) {
								return <EndScenarioMessage key={indx} timestamp={messageInfo?.timestamp} />;
							} else if (messageInfo.type === ScenarioMessageTypes.SERVICE) {
								return (
									<ServiceMessage
										key={indx}
										data={messageInfo.data}
										timestamp={messageInfo.timestamp}
									/>
								);
							}
						})}
					</div>
				) : (
					<EmptyChat />
				)}
				{showScrollButton && (
					<Button
						className="!p-0 absolute top-1 left-1/2 !px-2 !w-fit h-[25px] rounded-full -translate-x-1/2 transition"
						color={ButtonColor.ACTION_SECONDARY}
						image={<Icon className="w-4 h-4" icon={faArrowDown} />}
						title=""
						onClick={scrollToBottom}
					></Button>
				)}
			</div>
			{input !== undefined ? (
				input
			) : (
				<ChatInputField
					chatInputClassName={chatInputClassName}
					chatPermissions={chatPermissions}
					isProtocol={type !== "chat"}
					sendMessage={sendMessage}
				/>
			)}
		</div>
	);
}
